import React, { useState, useEffect } from "react";
import { Analytics } from "@vercel/analytics/react";
import { SpeedInsights } from "@vercel/speed-insights/react";
import "./App.css";
import ContactForm from "./ContactForm";
import ChatBot from "./ChatBot";
import LLM_API_CONFIG from "./config/llmApiConfig";

function App() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState("home");
  const [scrollY, setScrollY] = useState(0);

  // Handle scroll events for smooth section transitions and navbar effects
  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);

      // Determine active section based on scroll position
      const sections = document.querySelectorAll("section");
      sections.forEach((section) => {
        const sectionTop = section.offsetTop - 100;
        const sectionBottom = sectionTop + section.offsetHeight;
        if (scrollY >= sectionTop && scrollY < sectionBottom) {
          setActiveSection(section.id);
        }
      });
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [scrollY]);

  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);

  return (
    <div className="App">
      {/* Navigation Bar */}
      <nav className={`navbar ${scrollY > 50 ? "navbar-scrolled" : ""}`}>
        <div className="container">
          <div className="navbar-brand">
            <h1>ISLAND</h1>
            <p>ASSETS</p>
          </div>

          <div className="navbar-menu-toggle" onClick={toggleMenu}>
            <div className={`menu-icon ${isMenuOpen ? "open" : ""}`}>
              <span></span>
              <span></span>
              <span></span>
            </div>
          </div>

          <ul className={`navbar-links ${isMenuOpen ? "active" : ""}`}>
            <li>
              <a
                href="#home"
                className={activeSection === "home" ? "active" : ""}
                onClick={() => setIsMenuOpen(false)}
              >
                Home
              </a>
            </li>
            <li>
              <a
                href="#about"
                className={activeSection === "about" ? "active" : ""}
                onClick={() => setIsMenuOpen(false)}
              >
                About Us
              </a>
            </li>
            <li>
              <a
                href="#capabilities"
                className={activeSection === "capabilities" ? "active" : ""}
                onClick={() => setIsMenuOpen(false)}
              >
                Capabilities
              </a>
            </li>
            <li>
              <a
                href="#contact"
                className={activeSection === "contact" ? "active" : ""}
                onClick={() => setIsMenuOpen(false)}
              >
                Contact
              </a>
            </li>
          </ul>
        </div>
      </nav>

      {/* Hero Section */}
      <section id="home" className="hero-section">
        <div className="container">
          <div className="hero-content">
            <h1 className="hero-title">
              <span className="line">Strategic Solutions</span>
              <span className="line">for Complex Challenges</span>
            </h1>
            <p className="hero-subtitle">
              We transform organizations through data-driven insights and
              innovative strategies
            </p>
            <a href="#contact" className="cta-button">
              Schedule a Consultation
            </a>
          </div>
          <div className="hero-backdrop">
            <div className="backdrop-element"></div>
            <div className="backdrop-element"></div>
          </div>
        </div>
      </section>

      {/* About Us Section */}
      <section id="about" className="about-section">
        <div className="container">
          <div className="section-header">
            <h6 className="section-preheading">Who We Are</h6>
            <h2 className="section-heading">About Us</h2>
            <div className="separator"></div>
          </div>

          <div className="about-content">
            <div className="about-text">
              <p className="featured-text">
                A boutique consultancy focused on delivering exceptional value
                through bespoke solutions.
              </p>
              <p>
                With experience across small, medium, and large firms that span
                finance, industrials and technology, we bring unparalleled
                expertise to every project. By leveraging cutting-edge research,
                advanced proprietary solutions, complex mathematical insights,
                and AI, we enable long-term partnerships that create
                transformative outcomes for our clients. Our commitment to
                transparent value-adding performance for our clients is
                represented in:
              </p>
              <div className="stats-container">
                <div className="stat-item">
                  <span className="stat-number">350+</span>
                  <span className="stat-label">Citations on Research</span>
                </div>
                <div className="stat-item">
                  <span className="stat-number">1</span>
                  <span className="stat-label">US Patent</span>
                </div>
                <div className="stat-item">
                  <span className="stat-number">20+</span>
                  <span className="stat-label">Projects Completed</span>
                </div>
              </div>
            </div>

            <div className="about-image">
              <div className="image-container">
                <img
                  src="/images/about-photo.jpeg"
                  alt="Dr. Ryan Parker, Managing Partner"
                  className="about-img"
                />
                <div className="image-caption">
                  <strong>Dr. Ryan Parker</strong>
                  <br />
                  Managing Partner, Island Assets
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Capabilities Section */}
      <section id="capabilities" className="capabilities-section">
        <div className="container">
          <div className="section-header">
            <h6 className="section-preheading">What We Do</h6>
            <h2 className="section-heading">Our Capabilities</h2>
            <div className="separator"></div>
          </div>

          <div className="capabilities-grid">
            <div className="capability-card">
              <div className="card-icon">
                {/* Brain/Circuit for AI & Data Science - Improved */}
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  width="36"
                  height="36"
                >
                  <path
                    fill="currentColor"
                    d="M13,13V21H11V13H3V11H11V3H13V11H21V13H13M12,8A1,1 0 0,0 11,9A1,1 0 0,0 12,10A1,1 0 0,0 13,9A1,1 0 0,0 12,8M12,14A1,1 0 0,0 11,15A1,1 0 0,0 12,16A1,1 0 0,0 13,15A1,1 0 0,0 12,14M8,12A1,1 0 0,0 7,13A1,1 0 0,0 8,14A1,1 0 0,0 9,13A1,1 0 0,0 8,12M16,12A1,1 0 0,0 15,13A1,1 0 0,0 16,14A1,1 0 0,0 17,13A1,1 0 0,0 16,12Z"
                  />
                </svg>
              </div>
              <h3>Mathematics, Data Science & AI</h3>
              <p>The techniques that form the backbone of our solutions.</p>
            </div>

            <div className="capability-card">
              <div className="card-icon">
                {/* Bar Chart for Factor Modelling - Improved */}
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  width="36"
                  height="36"
                >
                  <path
                    fill="currentColor"
                    d="M22,21H2V3H4V19H6V10H10V19H12V6H16V19H18V14H22V21Z"
                  />
                </svg>
              </div>
              <h3>Factor Modelling</h3>
              <p>Finding hidden drivers of business outcomes.</p>
            </div>

            <div className="capability-card">
              <div className="card-icon">
                {/* Process Optimization - Improved */}
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  width="36"
                  height="36"
                >
                  <path
                    fill="currentColor"
                    d="M19.14,12.94c0.04-0.3,0.06-0.61,0.06-0.94c0-0.32-0.02-0.64-0.07-0.94l2.03-1.58c0.18-0.14,0.23-0.41,0.12-0.61 l-1.92-3.32c-0.12-0.22-0.37-0.29-0.59-0.22l-2.39,0.96c-0.5-0.38-1.03-0.7-1.62-0.94L14.4,2.81c-0.04-0.24-0.24-0.41-0.48-0.41 h-3.84c-0.24,0-0.43,0.17-0.47,0.41L9.25,5.35C8.66,5.59,8.12,5.92,7.63,6.29L5.24,5.33c-0.22-0.08-0.47,0-0.59,0.22L2.74,8.87 C2.62,9.08,2.66,9.34,2.86,9.48l2.03,1.58C4.84,11.36,4.8,11.69,4.8,12s0.02,0.64,0.07,0.94l-2.03,1.58 c-0.18,0.14-0.23,0.41-0.12,0.61l1.92,3.32c0.12,0.22,0.37,0.29,0.59,0.22l2.39-0.96c0.5,0.38,1.03,0.7,1.62,0.94l0.36,2.54 c0.05,0.24,0.24,0.41,0.48,0.41h3.84c0.24,0,0.44-0.17,0.47-0.41l0.36-2.54c0.59-0.24,1.13-0.56,1.62-0.94l2.39,0.96 c0.22,0.08,0.47,0,0.59-0.22l1.92-3.32c0.12-0.22,0.07-0.47-0.12-0.61L19.14,12.94z M12,15.6c-1.98,0-3.6-1.62-3.6-3.6 s1.62-3.6,3.6-3.6s3.6,1.62,3.6,3.6S13.98,15.6,12,15.6z"
                  />
                </svg>
              </div>
              <h3>Optimisation</h3>
              <p>Optimise business processes to drive profitability.</p>
            </div>

            <div className="capability-card">
              <div className="card-icon">
                {/* Automation Icon - Improved */}
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  width="36"
                  height="36"
                >
                  <path
                    fill="currentColor"
                    d="M12,16A3,3 0 0,1 9,13C9,11.88 9.61,10.9 10.5,10.39L20.21,4.77L14.68,14.35C14.18,15.33 13.17,16 12,16M12,3C13.81,3 15.5,3.5 16.97,4.32L14.87,5.53C14,5.19 13,5 12,5A8,8 0 0,0 4,13C4,15.21 4.89,17.21 6.34,18.65H6.35C6.74,19.04 6.74,19.67 6.35,20.06C5.96,20.45 5.32,20.45 4.93,20.07V20.07C3.12,18.26 2,15.76 2,13A10,10 0 0,1 12,3M22,13C22,15.76 20.88,18.26 19.07,20.07V20.07C18.68,20.45 18.05,20.45 17.66,20.06C17.27,19.67 17.27,19.04 17.66,18.65V18.65C19.11,17.2 20,15.21 20,13C20,12 19.81,11 19.46,10.1L20.67,8C21.5,9.5 22,11.18 22,13Z"
                  />
                </svg>
              </div>
              <h3>Automation</h3>
              <p>Increase the proportion of value-adding work.</p>
            </div>

            <div className="capability-card">
              <div className="card-icon">
                {/* Cloud Icon for Digital Transformation - Improved */}
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  width="36"
                  height="36"
                >
                  <path
                    fill="currentColor"
                    d="M19.35,10.03C18.67,6.59 15.64,4 12,4C9.11,4 6.6,5.64 5.35,8.03C2.34,8.36 0,10.9 0,14A6,6 0 0,0 6,20H19A5,5 0 0,0 24,15C24,12.36 21.95,10.22 19.35,10.03Z"
                  />
                </svg>
              </div>
              <h3>Digital Transformation</h3>
              <p>
                Modernize your business with cutting-edge digital strategies and
                implementation support.
              </p>
            </div>

            <div className="capability-card">
              <div className="card-icon">
                {/* Line Chart for Forecasting - Improved */}
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  width="36"
                  height="36"
                >
                  <path
                    fill="currentColor"
                    d="M3.5,18.49L9.5,12.48L13.5,16.48L22,6.92L20.59,5.51L13.5,13.48L9.5,9.48L2,16.99L3.5,18.49Z"
                  />
                </svg>
              </div>
              <h3>Forecasting</h3>
              <p>Deliver risk-managed forecasting and predictive insights.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="contact-section">
        <div className="container">
          <div className="section-header">
            <h6 className="section-preheading">Get In Touch</h6>
            <h2 className="section-heading">Contact Us</h2>
            <div className="separator"></div>
          </div>

          <div className="contact-content">
            <div className="contact-info">
              <h3>Ready to transform your business?</h3>
              <p>
                Our team is ready to help you tackle your most challenging
                business problems. Reach out to schedule a consultation.
              </p>

              <div className="contact-details">
                <div className="contact-item">
                  <div className="contact-icon">
                    <svg viewBox="0 0 24 24" width="24" height="24">
                      <path
                        fill="currentColor"
                        d="M20,8L12,13L4,8V6L12,11L20,6M20,4H4C2.89,4 2,4.89 2,6V18A2,2 0 0,0 4,20H20A2,2 0 0,0 22,18V6C22,4.89 21.1,4 20,4Z"
                      />
                    </svg>
                  </div>
                  <div className="contact-text">
                    <h4>Email</h4>
                    <p>ryan@islandassets.com.au</p>
                  </div>
                </div>

                {/*
                <div className="contact-item">
                  <div className="contact-icon">
                    <svg viewBox="0 0 24 24" width="24" height="24">
                      <path
                        fill="currentColor"
                        d="M6.62,10.79C8.06,13.62 10.38,15.94 13.21,17.38L15.41,15.18C15.69,14.9 16.08,14.82 16.43,14.93C17.55,15.3 18.75,15.5 20,15.5A1,1 0 0,1 21,16.5V20A1,1 0 0,1 20,21A17,17 0 0,1 3,4A1,1 0 0,1 4,3H7.5A1,1 0 0,1 8.5,4C8.5,5.25 8.7,6.45 9.07,7.57C9.18,7.92 9.1,8.31 8.82,8.59L6.62,10.79Z"
                      />
                    </svg>
                  </div>
                  <div className="contact-text">
                    //<h4>Phone</h4>
                    //<p>+61 417 111 755</p>
                  </div>
                </div>
                  */}
              </div>
            </div>

            <div className="contact-form">
              <ContactForm />
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="container">
          <div className="footer-content">
            <div className="footer-brand">
              <h2>ISLAND</h2>
              <p>ASSETS</p>
            </div>
            <div className="footer-links">
              <ul>
                <li>
                  <a href="#home">Home</a>
                </li>
                <li>
                  <a href="#about">About Us</a>
                </li>
                <li>
                  <a href="#capabilities">Capabilities</a>
                </li>
                <li>
                  <a href="#contact">Contact</a>
                </li>
              </ul>
            </div>
            {/*
            <div className="footer-social">
              <a href="#" aria-label="LinkedIn">
                <svg viewBox="0 0 24 24" width="24" height="24">
                  <path
                    fill="currentColor"
                    d="M19,3A2,2 0 0,1 21,5V19A2,2 0 0,1 19,21H5A2,2 0 0,1 3,19V5A2,2 0 0,1 5,3H19M18.5,18.5V13.2A3.26,3.26 0 0,0 15.24,9.94C14.39,9.94 13.4,10.46 12.92,11.24V10.13H10.13V18.5H12.92V13.57C12.92,12.8 13.54,12.17 14.31,12.17A1.4,1.4 0 0,1 15.71,13.57V18.5H18.5M6.88,8.56A1.68,1.68 0 0,0 8.56,6.88C8.56,5.95 7.81,5.19 6.88,5.19A1.69,1.69 0 0,0 5.19,6.88C5.19,7.81 5.95,8.56 6.88,8.56M8.27,18.5V10.13H5.5V18.5H8.27Z"
                  />
                </svg>
              </a>
              <a href="#" aria-label="Twitter">
                <svg viewBox="0 0 24 24" width="24" height="24">
                  <path
                    fill="currentColor"
                    d="M22.46,6C21.69,6.35 20.86,6.58 20,6.69C20.88,6.16 21.56,5.32 21.88,4.31C21.05,4.81 20.13,5.16 19.16,5.36C18.37,4.5 17.26,4 16,4C13.65,4 11.73,5.92 11.73,8.29C11.73,8.63 11.77,8.96 11.84,9.27C8.28,9.09 5.11,7.38 3,4.79C2.63,5.42 2.42,6.16 2.42,6.94C2.42,8.43 3.17,9.75 4.33,10.5C3.62,10.5 2.96,10.3 2.38,10C2.38,10 2.38,10 2.38,10.03C2.38,12.11 3.86,13.85 5.82,14.24C5.46,14.34 5.08,14.39 4.69,14.39C4.42,14.39 4.15,14.36 3.89,14.31C4.43,16 6,17.26 7.89,17.29C6.43,18.45 4.58,19.13 2.56,19.13C2.22,19.13 1.88,19.11 1.54,19.07C3.44,20.29 5.7,21 8.12,21C16,21 20.33,14.46 20.33,8.79C20.33,8.6 20.33,8.42 20.32,8.23C21.16,7.63 21.88,6.87 22.46,6Z"
                  />
                </svg>
              </a>
              <a href="#" aria-label="Facebook">
                <svg viewBox="0 0 24 24" width="24" height="24">
                  <path
                    fill="currentColor"
                    d="M12 2.04C6.5 2.04 2 6.53 2 12.06C2 17.06 5.66 21.21 10.44 21.96V14.96H7.9V12.06H10.44V9.85C10.44 7.34 11.93 5.96 14.22 5.96C15.31 5.96 16.45 6.15 16.45 6.15V8.62H15.19C13.95 8.62 13.56 9.39 13.56 10.18V12.06H16.34L15.89 14.96H13.56V21.96A10 10 0 0 0 22 12.06C22 6.53 17.5 2.04 12 2.04Z"
                  />
                </svg>
              </a>
            </div>
                */}
          </div>
          <div className="footer-bottom">
            <p>&copy; 2025 Island Assets. All Rights Reserved.</p>
          </div>
        </div>
      </footer>
      <ChatBot
        apiConfig={LLM_API_CONFIG}
        systemPrompt={LLM_API_CONFIG.systemPrompt}
      />
    </div>
  );
}

export default App;
